import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { oracleGet } from "../lib/api";

const usd = (v) => `${v < 0 ? "-" : ""}$${Math.abs(Number(v || 0)).toFixed(2)}`;
const tone = (v) => (v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-cyan-200/60");

const Bet = ({ p }) => (
  <div className="grid grid-cols-12 gap-2 text-[11px] py-1 border-b border-[#ffffff08]" data-testid="oracle-bet-row">
    <span className="col-span-5 font-mono text-cyan-50 truncate">{p.symbol}</span>
    <span className="col-span-2 text-cyan-300/60 uppercase">{p.side}</span>
    <span className="col-span-2 text-right text-cyan-100">${Number(p.notional || 0).toFixed(2)}</span>
    <span className="col-span-3 text-right text-cyan-200/60">{p.entry}¢ → {p.status === "closed" ? `${p.close_price}¢` : `${p.current ?? "—"}¢`}</span>
  </div>
);

export const NexusOracle = ({ userId }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { setBusy(true); try { setD(await oracleGet(userId)); } catch (e) {} finally { setBusy(false); } }, [userId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="rounded-2xl border border-[#ffffff14] bg-[#0a1020] p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-300/40" /></div>;
  const s = d.stats;
  const st = d.settings;
  return (
    <div className="rounded-2xl border border-[#ffffff14] bg-[#0a1020] p-4 space-y-3" data-testid="oracle-panel">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-cyan-100 font-semibold text-sm"><Sparkles className="w-4 h-4 text-[#FFD700]" /> Kalshi Probability Desk: Ms Oracle, every 20 min, 24/7</p>
        <button onClick={load} data-testid="oracle-refresh" className="text-cyan-300/50 hover:text-cyan-100">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
      </div>
      <p className="text-[11px] text-cyan-200/60" data-testid="oracle-summary">
        {st.enabled ? "On" : "Off"}{!d.engine_on && " (staging copy, does not trade)"} · {s.bets} bets · {s.wins}/{s.settled} won · net <span className={tone(s.net_pnl)}>{usd(s.net_pnl)}</span> · {usd(s.open_cost)} in open bets
      </p>
      {d.weather_status && <p className="text-[11px] font-semibold" data-testid="oracle-weather-status"><span className={d.weather_paused ? "text-amber-300" : "text-emerald-300"}>{d.weather_status}</span> · <span className="text-emerald-300" data-testid="oracle-macro-status">{d.macro_status}</span></p>}
      <p className="text-[10px] text-cyan-200/40">
        Bets only when its probability beats Kalshi's price by {Math.round(st.edge_quant * 100)}¢ (weather) or {Math.round(st.edge_llm * 100)}¢ (macro). ${st.probation_bet} per bet until a category proves itself over 25 settled forecasts, then up to ${st.max_bet}. Stops for the day at -${st.daily_stop}.
        {(st.disabled_categories || []).length > 0 && ` Benched: ${st.disabled_categories.join(", ")}.`}
      </p>
      {d.open.length > 0 && <div><p className="text-[10px] uppercase tracking-wider text-cyan-300/40">Open bets</p>{d.open.map((p) => <Bet key={p.id} p={p} />)}</div>}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-cyan-300/40">Best value right now {d.scanned_at ? `· ${String(d.scanned_at).slice(11, 16)} UTC` : ""}</p>
        <div className="max-h-56 overflow-y-auto">
          {d.board.slice(0, 12).map((b) => (
            <div key={b.ticker + b.kind} className="text-[11px] py-1 border-b border-[#ffffff08]" data-testid="oracle-board-row">
              <p className="text-cyan-50"><span className="font-mono">{b.ticker}</span> <span className="text-cyan-300/50">{b.sub}</span></p>
              <p className="text-cyan-200/60">
                Buy <b className="uppercase">{b.side}</b> at {Math.round(b.ask * 100)}¢ · our YES chance {Math.round(b.p_yes * 100)}% ·
                <span className={b.edge > 0 ? " text-emerald-300" : " text-rose-300"}> edge {Math.round(b.edge * 100)}¢</span> · {b.kind} · {b.why}
              </p>
            </div>
          ))}
          {d.board.length === 0 && <p className="text-cyan-200/40 text-xs py-2" data-testid="oracle-board-empty">No markets priced yet.</p>}
        </div>
      </div>
    </div>
  );
};
